/**
 * VideoPlayer — modal that plays a Learn video.
 *
 * Selects between:
 *  - <iframe> for YouTube / Vimeo external_url
 *  - <video> that streams from /api/learn/stream/{id} for self-hosted uploads
 */
import { useEffect } from "react";
import { X } from "lucide-react";
import {
  streamUrlFor,
  youTubeIdFrom,
  vimeoIdFrom,
  isYouTubeUrl,
  isVimeoUrl,
} from "@/lib/learn/api";

const backdrop =
  "fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4";

export default function VideoPlayer({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!video) return null;

  return (
    <div
      className={backdrop}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="learn-video-player"
    >
      <div
        className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition"
          aria-label="Close"
          data-testid="learn-player-close"
        >
          <X className="h-5 w-5" />
        </button>

        {video.external_url && isYouTubeUrl(video.external_url) && (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube.com/embed/${youTubeIdFrom(video.external_url)}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}

        {video.external_url && isVimeoUrl(video.external_url) && (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://player.vimeo.com/video/${vimeoIdFrom(video.external_url)}?autoplay=1`}
            title={video.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        )}

        {video.external_url && !isYouTubeUrl(video.external_url) && !isVimeoUrl(video.external_url) && (
          // Direct MP4 / WebM link
          <video
            className="absolute inset-0 h-full w-full"
            src={video.external_url}
            controls
            autoPlay
            playsInline
            data-testid="learn-player-video"
          />
        )}

        {!video.external_url && video.storage_path && (
          // Self-hosted stream via backend range-aware endpoint
          <video
            className="absolute inset-0 h-full w-full"
            src={streamUrlFor(video.id)}
            controls
            autoPlay
            playsInline
            preload="metadata"
            data-testid="learn-player-video"
          />
        )}
      </div>
    </div>
  );
}
